import React, { useState, useMemo } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Sun, Moon, Eye, EyeOff, Check, X, CheckCircle2, ArrowRight } from 'lucide-react';
import { z } from 'zod';

const emailSchema = z.string().email('Por favor, insira um e-mail válido');

interface PasswordValidation {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecialCharacter: boolean;
}

const validatePassword = (password: string): PasswordValidation => ({
  minLength: password.length >= 8,
  hasUppercase: /[A-Z]/.test(password),
  hasLowercase: /[a-z]/.test(password),
  hasNumber: /[0-9]/.test(password),
  hasSpecialCharacter: /[!@#$%^&*(),.?":{}|<>]/.test(password),
});

const isPasswordValid = (v: PasswordValidation) =>
  v.minLength && v.hasUppercase && v.hasLowercase && v.hasNumber && v.hasSpecialCharacter;

const Signup: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string; password?: string }>({});
  const [touched, setTouched] = useState<{ name?: boolean; email?: boolean; password?: boolean }>({});

  const { signup, isAuthenticated, isLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { toast } = useToast();

  const passwordValidation = useMemo(() => validatePassword(password), [password]);
  const passwordIsValid = useMemo(() => isPasswordValid(passwordValidation), [passwordValidation]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const validateForm = (): boolean => {
    const newErrors: { name?: string; email?: string; password?: string } = {};
    if (!name.trim()) newErrors.name = 'Nome é obrigatório';
    const emailResult = emailSchema.safeParse(email);
    if (!emailResult.success) newErrors.email = emailResult.error.errors[0].message;
    if (!passwordIsValid) newErrors.password = 'A senha não atende aos requisitos';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, email: true, password: true });
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await signup(email, password, name);
      toast({ title: 'Conta criada!', description: 'Bem-vindo ao Patri. Você já está conectado.' });
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: 'Falha ao criar conta',
        description: error instanceof Error ? error.message : 'Por favor, tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const ValidationItem = ({ valid, label }: { valid: boolean; label: string }) => (
    <div className="flex items-center gap-2">
      {valid
        ? <Check className="h-3 w-3 text-foreground shrink-0" />
        : <X className="h-3 w-3 text-muted-foreground/50 shrink-0" />}
      <span className={`text-xs ${valid ? 'text-foreground' : 'text-muted-foreground/60'}`}>{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-[2fr_3fr]">
      {/* Left panel — product surface */}
      <div className="hidden lg:flex flex-col justify-between bg-card border-r border-border p-10">
        <div>
          <div className="flex items-center gap-2.5 mb-16">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm font-display">P</span>
            </div>
            <span className="text-foreground font-semibold">Patri</span>
          </div>

          <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground mb-3">
            Conta grátis
          </p>
          <h2 className="font-display font-bold text-foreground leading-[1.05] tracking-tight mb-4 text-[3rem] xl:text-[3.5rem]">
            Comece hoje.<br />É gratuito.
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed max-w-xs">
            Crie sua conta em segundos. Sem plano pago, sem planilha.
          </p>
        </div>

        <div className="space-y-3">
          {[
            'Conta protegida com autenticação segura',
            'Categorias ilimitadas de receita e despesa',
            'Importação de extrato OFX em um clique',
          ].map((text) => (
            <div key={text} className="flex items-center gap-3 text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-foreground/60 shrink-0" />
              <span className="text-sm">{text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex flex-col items-center justify-center p-5 sm:p-8 bg-background relative">
        <button
          onClick={toggleTheme}
          className="absolute top-5 right-5 p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
        >
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex lg:hidden items-center gap-2.5 mb-8">
            <div className="h-7 w-7 rounded-md bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-xs font-display">P</span>
            </div>
            <span className="text-foreground font-semibold">Patri</span>
          </div>

          <div className="mb-8">
            <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground mb-2 lg:hidden">
              Conta grátis
            </p>
            <h1 className="font-display font-bold text-foreground leading-[1.05] tracking-tight mb-1.5 text-[2rem]">
              Criar conta
            </h1>
            <p className="text-muted-foreground text-sm">Preencha seus dados para começar</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-sm font-medium">Nome</Label>
              <Input
                id="name"
                type="text"
                placeholder="Seu nome"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched((p) => ({ ...p, name: true }))}
                className={touched.name && errors.name ? 'border-destructive' : ''}
                disabled={isSubmitting}
              />
              {touched.name && errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium">E-mail</Label>
              <Input
                id="email"
                type="email"
                placeholder="voce@exemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched((p) => ({ ...p, email: true }))}
                className={touched.email && errors.email ? 'border-destructive' : ''}
                disabled={isSubmitting}
              />
              {touched.email && errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-medium">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setTouched((p) => ({ ...p, password: true }))}
                  className={touched.password && !passwordIsValid ? 'border-destructive pr-10' : 'pr-10'}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {/* Password requirements — always visible */}
              <div className="mt-2 p-3 rounded-md bg-muted/60 grid grid-cols-2 gap-1.5">
                <ValidationItem valid={passwordValidation.minLength} label="8+ caracteres" />
                <ValidationItem valid={passwordValidation.hasUppercase} label="Maiúscula" />
                <ValidationItem valid={passwordValidation.hasLowercase} label="Minúscula" />
                <ValidationItem valid={passwordValidation.hasNumber} label="Número" />
                <ValidationItem valid={passwordValidation.hasSpecialCharacter} label="Caractere especial" />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 mt-2"
            >
              {isSubmitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Criando conta...</>
              ) : (
                <><span>Criar conta</span><ArrowRight className="h-3.5 w-3.5" /></>
              )}
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Já tem uma conta?{' '}
            <Link to="/login" className="text-primary font-medium hover:opacity-80 transition-opacity">
              Entrar
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
